import React, { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';

import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';

import UtilFunctions from '~utils/functions';

import * as FormStyles from './FormCreateCustomerTransferModal.module.scss';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import { BranchStatusEnum, CustomerAccFundTypeEnum, CustomerAccTypeEnum, FundTypeEnum, GoldTypeEnum, MetalTypeEnum, SilverTypeEnum, TransactionActionEnum } from '~utils/enums';
import { TCreateCustomerTransferFinalDraftsParams } from '~types/feat/customerTransfer';
import { TDropdownOption } from '~types/components/labelledDropdown';
import { TInventoryFilter } from '~types/feat/gold';

type TFormCreateCustomerTransferModalProps = {
    submitButtonLabel: string;
}

const FormCreateCustomerTransferModal: FC<TFormCreateCustomerTransferModalProps> = ({ submitButtonLabel }) => {
    const dispatch = useAppDispatch();
    const { Misc: { blockEnterKey } } = UtilFunctions;

    const customerList = useAppSelector((state) => state.customer.allCustomers);
    const customerDetails = useAppSelector((state) => state.customer.customerDetails);
    const customerInventory = useAppSelector((state) => state.customer.customerInventory);

    const serialNumberFilterParams = useAppSelector((state) => state.gold.params.serialNumberFilterParams);
    const inventoryFilterParams = useAppSelector((state) => state.gold.params.inventoryFilterParams);
    
    const inventoryList = useAppSelector((state) => state.gold.allInventories);
    const branchesList = useAppSelector((state) => state.branch.allBranches);
    const regionList = useAppSelector((state) => state.branch.allRegions);

    const searchCustomerIsLoading = useAppSelector(state => state.customer.loadings.getAllCustomers);

    const { allBranchAccess, branchCode } = useAppSelector((state) => state.auth.userDetails);

    const createFinalDraftIsLoading = useAppSelector(state => state.customerTransfer.loadings.createCustomerTransferFinalDrafts);
    const isError = useAppSelector(state => state.customerTransfer.errors.createCustomerTransferFinalDrafts);

    const customerTransferDraftId = useAppSelector(state => state.customerTransfer.customerTransferDraftId);
    const createDraftIsLoading = useAppSelector(state => state.customerTransfer.loadings.createCustomerTransferDrafts);

    const { displayName } = useAppSelector(state => state.auth.userDetails);

    const [senderName, setSendername] = useState('');  
    const [receiverName, setReceiverName] = useState(''); 
    const [disableCustomerSearch, setDisableCustomerSearch] = useState(false);
    const [productSearch, setProductSearch] = useState('');
    const [gettingCustomers, setGettingCustomers] = useState(false);
    const [selectedBranchCode, setSelectedBranchCode] = useState('');
    const [activeCustomerSearch, setActiveCustomerSearch] = useState<'sender' | 'receiver' | null>(null);

    const createCustomerTransferFormValidation = Yup.object().shape({
        senderName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Sender name is required'),
        receiverName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Receiver name is required'),
        senderIdNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Sender ID is required'),
        senderCIFNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Sender CIF is required'),
        senderCustomerId: Yup.number().required('Sender is required').moreThan(0, 'Please select a sender'),
        receiverCustomerId: Yup.number().required('Receiver is required').moreThan(0, 'Please select a receiver')
            .test(
                'different-customer',
                'Sender and receiver cannot be the same customer',
                function (value) {
                    if (!value || !this.parent.senderCustomerId) return true;

                    return Number(value) !== Number(this.parent.senderCustomerId);
                }
            ),
        receiverIdNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Receiver ID is required'),
        receiverCIFNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Receiver CIF is required'),
        branchName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Branch name is required')
            .test('check-branch-name',
                'Selected branch does not exist',
                function (value) {
                    if (!value) return false;
                    return branchesList.some((branch) => branch.branchName === value);
                }
            ),
        branchCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().required('Branch code is required'),
        region: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Region is required'),
        metalType: Yup.number().required('Metal Type is required').min(1, 'Metal Type is required'),
        products: Yup.object().shape({
            productId: Yup.number().required('Product is required'),
            productName: Yup.string().required('Product Name is required'),
            availableQuantity: Yup.mixed(),
            amount: Yup.number()
                .transform((value, originalValue) =>
                    originalValue === '' ? undefined : value
                )
                .typeError('Total weight must be a number')
                .required('Total weight is required')
                .positive('Total weight must be greater than 0')
                .min(0.001, 'Minimum total weight is 0.001 g')
                .max(1000000, 'Total weight must not exceed 1000000 grams')
                .test('no-exponential-notation',
                    'Exponential notation is not allowed',
                    function (value) {
                        const original = this.originalValue;

                        if (original === undefined || original === null || original === '') {
                            return true;
                        }

                        return !/e/i.test(String(original));
                    }
                )
                .test('is-within-balance',
                    'Total weight must not exceed available balance',
                    function (value) {
                        if (value === undefined || value === null) return true;

                        const { availableQuantity } = this.parent;

                        const balance = Number(
                            String(availableQuantity ?? '').replace(/,/g, '').split(' ')[0]
                        );

                        if (!Number.isFinite(balance)) return false;

                        return value <= balance;
                    }
                ),
        }),
        branchTellerName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace(),
        salesPersonnelName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace(),
        referralName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace(),
    });

    const createCustomerTransferFormInitialValues = {
        senderCustomerId: 0,
        senderName: '',
        senderIdNo: '',
        senderCIFNo: '',
        fundType: 0,
        productName: '',
        metalType: 0,
        serialNumbers: [],
        totalWeight: 0,
        receiverCustomerId: 0,
        receiverName: '',
        receiverIdNo: '',
        receiverCIFNo: '',
        branchCode: '',
        products: {
            productId: 0,
            productName: '',
            goldType: 0,
            silverType: 0,
            availableQuantity: 'Please select customer first',
            amount: '',
        },
        branchName: '',
        region: '',
        branchTellerName: '',
        referralName: '',
        salesPersonnelName: '',
    };

    const formik = useFormik({
        initialValues: createCustomerTransferFormInitialValues,
        validationSchema: createCustomerTransferFormValidation,
        enableReinitialize: true,
        onSubmit: (values) => {
            const selectedBranch = branchesList.find(branch => branch.branchCode === values.branchCode);

            const payload: TCreateCustomerTransferFinalDraftsParams = {
                senderCustomerId: Number(values.senderCustomerId),
                receiverCustomerId: Number(values.receiverCustomerId),
                productId: Number(values.products.productId),
                weight: Number(values.products.amount),
                branchId: Number(selectedBranch?.branchId),
                ...(values.salesPersonnelName?.trim() && {
                    salesPersonnelName: values.salesPersonnelName.trim(),
                }),
                ...(values.referralName?.trim() && {
                    referralName: values.referralName.trim(),
                }),
            };

            dispatch(combinedActions.customerTransferCreateCustomerTransferFinalDraftAttempt(payload));
        },
    });

    const getAvailablePaperBalance = (metalType: number) => {
        if (!metalType) return null;

        const totalBalance = customerInventory.filter(inventory => {
            if (metalType === MetalTypeEnum.Gold) {
                return (
                    inventory.metalType === MetalTypeEnum.Gold &&
                    inventory.goldType === GoldTypeEnum.PaperGold
                );
            }

            if (metalType === MetalTypeEnum.Silver) {
                return (
                    inventory.metalType === MetalTypeEnum.Silver &&
                    inventory.silverType === SilverTypeEnum.PaperSilver
                );
            }

            return false;
        }).reduce((total, inventory) => total + Number(inventory.amount ?? 0), 0);

        return `${totalBalance.toFixed(3)} g`;
    };

    useEffect(() => {
        const searchQuery = activeCustomerSearch === 'sender' ? senderName : activeCustomerSearch === 'receiver' ? receiverName : '';

        if (!searchQuery || disableCustomerSearch) {
            setGettingCustomers(false);
            setDisableCustomerSearch(false);
            return;
        }

        setGettingCustomers(true);

        const timeout = setTimeout(() => {
            dispatch(combinedActions.customerGetAllCustomersAttempt({ searchQuery }));
        }, 1000);

        return () => {
            clearTimeout(timeout);
        };
    }, [senderName, receiverName, activeCustomerSearch]);

    useEffect(() => {
        // to set loading state (debouncing causes loading state to also delay hence we use a local loading state)
        if (!searchCustomerIsLoading) {
            setGettingCustomers(false);
        }
    }, [searchCustomerIsLoading]);

    // ? to set branch based on user details
    useEffect(() => {
        if (!allBranchAccess) {
            const branch = branchesList.find((branchOption) => branchOption.branchCode === branchCode);
            const branchRegion = regionList.find((regionOption) => regionOption.regionId === branch?.regionId);

            if (branch) {
                setSelectedBranchCode(branch.branchCode);
                dispatch(combinedActions.goldSetSerialNumberFilterParams({
                    ...serialNumberFilterParams,
                    branchCode: branch.branchCode,
                }));

                formik.setFieldValue('branchName', branch.branchName);
                formik.setFieldValue('branchCode', branch.branchCode);
            }

            if (branchRegion) {
                formik.setFieldValue('region', branchRegion.regionName);
            }
        }
    }, [branchesList]);

    // ? to get selected branch inventories
    // useEffect(() => {
    //     if (selectedBranchCode && !formik.values.products.productId) {
    //         dispatch(combinedActions.goldSetInventoryFilterParams({ ...inventoryFilterParams, branchCode: selectedBranchCode }));
    //         dispatch(combinedActions.goldGetAllInventoriesAttempt());

    //         formik.setValues({
    //             ...formik.values,
    //             branchCode: selectedBranchCode,
    //             products: {
    //                 productId: 0,
    //                 silverType: 0,
    //                 goldType: 0,
    //                 productName: '',
    //                 availableQuantity: 'Please select product first',
    //                 amount: '',
    //             }
    //         });
    //     }
    // }, [selectedBranchCode]);

    const senderReceiverOptions = customerList.map((customer) => ({
        label: `${customer.name} - ${customer.idNo}  (${CustomerAccFundTypeEnum[customer.fundType]}) (${CustomerAccTypeEnum[customer.accountType]})${customer.accountType === CustomerAccTypeEnum.Joint ? (' - ' + customer.childName) : ''}`,
        value: customer.customerId,
    }));

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active).map((branch) => ({
        label: branch.branchName,
        value: branch.branchCode,
    }));

    const regionOptions = regionList.map((region) => ({
        label: region.regionName,
        value: region.regionName,
    }));

    const handleOnSearchSender = (event: React.ChangeEvent<HTMLInputElement>) => {
        setActiveCustomerSearch('sender');
        setSendername(event.target.value);
    };

    const handleOnSearchReceiver = (event: React.ChangeEvent<HTMLInputElement>) => {
        setActiveCustomerSearch('receiver');
        setReceiverName(event.target.value);
    };

    const handleOnSelectSender = (option: TDropdownOption) => {
        const customer = customerList.find((customerOption) => customerOption.customerId === option.value);

        if (!customer) return;

        setDisableCustomerSearch(true);

        formik.setValues({
            ...formik.values,
            senderCustomerId: Number(customer.customerId),
            senderName: customer.name,
            senderIdNo: customer.idNo,
            senderCIFNo: customer.CIFNo,
            fundType: customer.fundType,
            metalType: 0,
            products: {
                productId: 0,
                productName: '',
                goldType: 0,
                silverType: 0,
                availableQuantity: 'Please select product first',
                amount: '',
            }
        });

        handleCustomerSelected(customer.customerId);

        setProductSearch('');

        getProductSerialNumber(undefined, Number(customer.customerId));
    };

    const handleOnSelectReceiver = (option: TDropdownOption) => {
        const customer = customerList.find(customerOption => Number(customerOption.customerId) === Number(option.value));

        if (!customer) return;

        const isSameCustomer = Number(customer.customerId) === Number(formik.values.senderCustomerId);

        setDisableCustomerSearch(true);
        setReceiverName(option.label);

        formik.setValues({
            ...formik.values,
            receiverCustomerId: Number(customer.customerId),
            receiverName: isSameCustomer ? '' : customer.name,
            receiverIdNo: isSameCustomer ? '' : customer.idNo,
            receiverCIFNo: isSameCustomer ? '' : customer.CIFNo,
        });

        formik.setFieldTouched('receiverCustomerId', true, false);
    };

    const handleCustomerSelected = (customerId: number) => {
        dispatch(combinedActions.customerGetCustomerDetailsAttempt({ customerId }));
    };

    const getProductSerialNumber = (product?: TInventoryFilter, customerId?: number) => {
        if (product) {
            dispatch(combinedActions.goldSetSerialNumberFilterParams({
                ...serialNumberFilterParams,
                productId: product.productId,
            }));
        }
        if (customerId) dispatch(combinedActions.customerGetCustomerInventoryAttempt({ customerId: customerId }));
    };

    const getCustomerBalance = (selectedProduct?: TInventoryFilter) => {
        if (!selectedProduct) {
            formik.setFieldValue('products.availableQuantity', 'Please select product first');
            return;
        }

        const selectedInventory = customerInventory.find(inventory => inventory.productId === selectedProduct.productId);

        const isPaperProduct = selectedProduct.goldType === GoldTypeEnum.PaperGold || selectedProduct.silverType === SilverTypeEnum.PaperSilver;

        if (!isPaperProduct) return;

        const availableQuantity = selectedInventory ? `${Number(selectedInventory.amount ?? 0).toFixed(3)} g` : '0.000 g';

        formik.setFieldValue('products', {
            ...formik.values.products,
            productId: Number(selectedProduct.productId),
            productName: selectedProduct.productName,
            goldType: selectedProduct.goldType ?? 0,
            silverType: selectedProduct.silverType ?? 0,
            availableQuantity,
            amount: '',
            serialNo: '',
            totalNetValue: '',
        });
    };

    const handleOnSearchProduct = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setProductSearch(value);

        const product = customerInventory.find(item => item.productName === value);

        if (!product) {
            formik.setFieldValue('products.productId', 0);
            formik.setFieldValue('products.productName', '');
        }
    };

    const handleOnSelectProduct = (option: TDropdownOption) => {
        const product = customerInventory.find(item => item.productId === Number(option.value));

        if (!product) return;

        if (formik.values.products.productId === product.productId) return;

        setProductSearch(product.productName);

        const availableQuantity = `${Number(product.amount ?? 0).toFixed(3)} g`;

        formik.setFieldValue('products', {
            ...formik.values.products,
            productId: product.productId,
            productName: product.productName,
            goldType: product.goldType ?? 0,
            silverType: product.silverType ?? 0,
            availableQuantity,
            amount: '',
        });
    };

    const handleOnSearchBranch = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'branchName', value: event.target.value } });

        const branch = branchesList.find((branchOption) => branchOption.branchName === event.target.value);
        const branchRegion = regionList.find((regionOption) => regionOption.regionId === branch?.regionId);

        if (!branch) {
            dispatch(combinedActions.goldClearInventoryFilterParams());
            formik.handleChange({ target: { name: 'region', value: '' } });
            formik.handleChange({ target: { name: 'branchCode', value: '' } });
            setSelectedBranchCode('');
            return;
        }

        if (branch) {
            setSelectedBranchCode(branch.branchCode);
            formik.handleChange({ target: { name: 'branchCode', value: branch.branchCode } });
            dispatch(combinedActions.goldSetSerialNumberFilterParams({
                ...serialNumberFilterParams,
                branchCode: branch.branchCode,
            }));
        }

        if (branchRegion) {
            formik.handleChange({ target: { name: 'region', value: branchRegion.regionName } });
        }
    };

    const handleOnSelectBranch = (option: TDropdownOption) => {
        formik.setFieldValue('branchName', option.label);
        formik.setFieldValue('branchCode', option.value);
        formik.setFieldTouched('branchName', false);

        const branch = branchesList.find((branchOption) => branchOption.branchCode === option.value);
        const branchRegion = regionList.find((regionOption) => regionOption.regionId === branch?.regionId);

        if (branch) {
            setSelectedBranchCode(branch.branchCode);
            dispatch(combinedActions.goldSetSerialNumberFilterParams({
                ...serialNumberFilterParams,
                branchCode: branch.branchCode,
            }));

            formik.setValues({
                ...formik.values,
                products: {
                    productId: 0,
                    silverType: 0,
                    goldType: 0,
                    productName: '',
                    availableQuantity: 'Please select product first',
                    amount: '',
                }
            }); // to reset product each time changing branch

        }

        if (branchRegion) {
            formik.handleChange({ target: { name: 'region', value: branchRegion.regionName } });
        }
    };

    const handleOnSearchRegion = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'region', value: event.target.value } });
    };

    const handleOnSelectRegion = (option: TDropdownOption) => {
        formik.handleChange({ target: { name: 'region', value: option.value } });
    };

    const metalTypeOptions = Object.entries(MetalTypeEnum)
        .filter(([k]) => isNaN(Number(k)))
        .map(([label, value]) => ({ label, value: String(value) }));

    const handleOnSelectMetalType = (option: TDropdownOption) => {
        const selectedMetalType = Number(option.value);

        const availableBalance = getAvailablePaperBalance(selectedMetalType);

        formik.setFieldValue('metalType', selectedMetalType);

        formik.setFieldValue('products', {
            ...createCustomerTransferFormInitialValues.products,
            availableQuantity: availableBalance ?? '0.000 g',
        });

        setProductSearch('');

        dispatch(combinedActions.goldSetInventoryFilterParams({
            ...inventoryFilterParams,
            metalType: selectedMetalType,
            branchCode: formik.values.branchCode,
            silverType: undefined,
            goldType: undefined,
        }));
    };

    const handleOnSearchMetalType = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.value) {
            formik.setFieldValue('metalType', 0);

            formik.setFieldValue('products', {
                ...createCustomerTransferFormInitialValues.products,
                availableQuantity: 'Please select product first',
            });

            setProductSearch('');
        }
    };

    const inventoryOptions = () => {
        if (!formik.values.senderCustomerId || !formik.values.metalType) {
            return [];
        }

        return customerInventory
            .filter(item => {
                if (item.metalType !== formik.values.metalType) {
                    return false;
                }

                if (item.metalType === MetalTypeEnum.Gold) {
                    return item.goldType === GoldTypeEnum.PaperGold;
                }

                if (item.metalType === MetalTypeEnum.Silver) {
                    return item.silverType === SilverTypeEnum.PaperSilver;
                }

                return false;
            })
            .map(item => ({
                label: item.productName,
                value: item.productId,
            }));
    };

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <LabelledDropdown
                    isFilter
                    label='Search for sender name or ID* : '
                    options={senderReceiverOptions}
                    value={senderName}
                    onChange={handleOnSearchSender}
                    onSelectOption={handleOnSelectSender}
                    noOptionsMessage={!senderName ? 'Search for sender name or ID' : 'No sender found'}
                    isLoading={gettingCustomers}
                    autoComplete='off'
                    optionSelected={!!senderReceiverOptions.find((option) => option.label === formik.values.senderName)}
                    inputTestId='transaction-customer-transfer-name-input-dropdown'
                    optionTestId='transaction-customer-transfer-name-options-list-dropdown'
                />

                <LabelledInput
                    id='senderName'
                    name='senderName'
                    label='Sender Name* :'
                    value={formik.values.senderName}
                    error={formik.touched.senderName ? formik.errors.senderName : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                    className={FormStyles['span-2']}
                    data-testid='transaction-customer-transfer-name-input'
                />

                <LabelledInput
                    id='senderIdNo'
                    name='senderIdNo'
                    label='Sender ID Number* :'
                    value={formik.values.senderIdNo}
                    error={formik.touched.senderIdNo ? formik.errors.senderIdNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledInput
                    id='senderCIFNo'
                    name='senderCIFNo'
                    label='Sender CIF No.* :'
                    value={formik.values.senderCIFNo}
                    error={formik.submitCount > 0 && formik.touched.senderCIFNo ? formik.errors.senderCIFNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledDropdown
                    inputTestId='transaction-customer-transfer-earmark-branchInput-dropdown'
                    optionTestId='transaction-customer-transfer-earmark-branchOption-dropdown'
                    id='branchName'
                    name='branchName'
                    label='Branch* :'
                    options={branchOptions}
                    value={formik.values.branchName}
                    error={formik.touched.branchName ? formik.errors.branchName : undefined}
                    onChange={handleOnSearchBranch}
                    onSelectOption={handleOnSelectBranch}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No branches found'
                    autoComplete='off'
                    optionSelected={!!branchOptions.find((option) => option.label === formik.values.branchName)}
                    disabled={!allBranchAccess}
                />

                <LabelledInput
                    id='branchCode'
                    name='branchCode'
                    label='Branch Code* :'
                    value={formik.values.branchCode}
                    error={formik.submitCount > 0 && formik.touched.branchCode ? formik.errors.branchCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledDropdown
                    id='region'
                    name='region'
                    label='Region* :'
                    options={regionOptions}
                    value={formik.values.region}
                    error={formik.submitCount > 0 && formik.touched.region ? formik.errors.region : undefined}
                    onChange={handleOnSearchRegion}
                    onSelectOption={handleOnSelectRegion}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No Region found'
                    className={FormStyles['span-2']}
                    autoComplete='off'
                    optionSelected={!!regionOptions.find((option) => option.label === formik.values.region)}
                    disabled
                />

                <LabelledDropdown
                    id='metalType'
                    name='metalType'
                    label='Metal Type *:'
                    options={metalTypeOptions}
                    value={metalTypeOptions.find(option => Number(option.value) === Number(formik.values.metalType))?.label ?? ''}
                    error={
                        formik.submitCount > 0 && formik.errors.metalType
                            ? formik.errors.metalType as string
                            : undefined
                    }
                    onSelectOption={handleOnSelectMetalType}
                    onChange={handleOnSearchMetalType}
                    noOptionsMessage='No metal type found'
                    autoComplete='off'
                    optionSelected={!!metalTypeOptions.find(
                        option => Number(option.value) === Number(formik.values.metalType)
                    )}
                    disabled={!selectedBranchCode || !formik.values.senderCustomerId}
                />

                <div></div>
                
                <LabelledDropdown
                    id='products.productName'
                    name='products.productName'
                    label='Product Name *: '
                    options={inventoryOptions().sort((a, b) => Number(a.value) - Number(b.value))}
                    value={productSearch}
                    error={formik.submitCount > 0 ? formik.errors.products?.productId || formik.errors.products?.productName : undefined}
                    onChange={handleOnSearchProduct}
                    onSelectOption={handleOnSelectProduct}
                    noOptionsMessage='No product found'
                    autoComplete='off'
                    optionSelected={!!inventoryOptions().find(option => option.value === formik.values.products.productId)}
                    disabled={!selectedBranchCode || !formik.values.senderCustomerId || !formik.values.metalType}
                />

                <div className={FormStyles['available-balance']}>
                    <Text label='Available balance:' />
                    <Text
                        testId='transaction-available-balance-label'
                        label={!formik.values.senderCustomerId
                            ? 'Please select customer first'
                            : !formik.values.products.productId
                                ? 'Please select product first'
                                : formik.values.products.availableQuantity ?? '0.000 g'
                        }
                        isBold
                    />
                </div>

                <LabelledInput
                    id='products.amount'
                    name='products.amount'
                    label='Total Weight (g)*:'
                    value={formik.values.products.amount}
                    error={formik.touched.products?.amount ? formik.errors.products?.amount : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!formik.values.senderCustomerId || !formik.values.products.productId}
                />

                <div></div>

                <LabelledDropdown
                    isFilter
                    label='Search for receiver name or ID* : '
                    options={senderReceiverOptions}
                    value={receiverName}
                    onChange={handleOnSearchReceiver}
                    onSelectOption={handleOnSelectReceiver}
                    error={formik.touched.receiverCustomerId || formik.submitCount > 0 ? formik.errors.receiverCustomerId : undefined}
                    noOptionsMessage={!receiverName ? 'Search for sender name or ID' : 'No sender found'}
                    isLoading={gettingCustomers}
                    autoComplete='off'
                    optionSelected={!!senderReceiverOptions.find((option) => option.label === formik.values.receiverName)}
                    inputTestId='transaction-customer-transfer-name-input-dropdown'
                    optionTestId='transaction-customer-transfer-name-options-list-dropdown'
                />

                <LabelledInput
                    id='receiverName'
                    name='receiverName'
                    label='Receiver Name* :'
                    value={formik.values.receiverName}
                    error={formik.touched.receiverName ? formik.errors.receiverName : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                    className={FormStyles['span-2']}
                    data-testid='transaction-customer-transfer-name-input'
                />

                <LabelledInput
                    id='receiverIdNo'
                    name='receiverIdNo'
                    label='Receiver ID Number* :'
                    value={formik.values.receiverIdNo}
                    error={formik.touched.receiverIdNo ? formik.errors.receiverIdNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledInput
                    id='receiverCIFNo'
                    name='receiverCIFNo'
                    label='Receiver CIF No.* :'
                    value={formik.values.receiverCIFNo}
                    error={formik.submitCount > 0 && formik.touched.receiverCIFNo ? formik.errors.receiverCIFNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledInput
                    id='branchTellerName'
                    name='branchTellerName'
                    label='Branch Teller Name :'
                    value={displayName}
                    error={formik.submitCount > 0 && formik.touched.branchTellerName ? formik.errors.branchTellerName : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled
                />

                <LabelledInput
                    id='salesPersonnelName'
                    name='salesPersonnelName'
                    label='Sales Personnel Name :'
                    value={formik.values.salesPersonnelName}
                    error={formik.submitCount > 0 && formik.touched.salesPersonnelName ? formik.errors.salesPersonnelName : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />

                <LabelledInput
                    id='referralName'
                    name='referralName'
                    label='Referral Name :'
                    value={formik.values.referralName}
                    error={formik.submitCount > 0 && formik.touched.referralName ? formik.errors.referralName : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={createFinalDraftIsLoading}
                            type='submit'
                            disabled={createFinalDraftIsLoading || createDraftIsLoading || !customerTransferDraftId}
                        />

                        <Text
                            label={isError}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default FormCreateCustomerTransferModal;
